'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { Link, Text, useTranslation, api, getCookie, setCookie } from '@weareconceptstudio/core';
import { AccountCustomCheckbox } from '@weareconceptstudio/account';
import { useUser } from '@weareconceptstudio/account';

//* Utils
import { subscribePopupCloseSVG } from './utils';

//* Helpers
import { config } from '@/helpers';
import { CLOSE_BOTTOM_POPUPS_EVENT } from '@/helpers/bottomPopups';

//* Styles
import SubscribePopupStyle from './style';

//* Components
import Button from '../Button';
import { Form, Input } from '@weareconceptstudio/form';

const SubscribePopup = () => {
	const pathname = usePathname();

	const { translate, selectedLang } = useTranslation();

	const { isLoggedIn } = useUser();

	const wrapperRef = useRef(null);

	//! States
	const [hide, setHide] = useState(false);
	const [step, setStep] = useState(1);
	const [isChecked, setIsChecked] = useState(false);
	const [btnDisabled, setBtnDisabled] = useState(true);
	const [onceAnim, setOnceAnim] = useState(false);

	const [email, setEmail] = useState('');

	useEffect(() => {
		let timeId;

		timeId = setTimeout(() => {
			setOnceAnim(true);
		}, 1800);

		return () => {
			clearTimeout(timeId);
		};
	}, []);

	const handleClose = () => {
		setHide(true);
		sessionStorage.setItem('subscribe_popup_closed', JSON.stringify(true));
	};

	useEffect(() => {
		const handler = () => setHide(true);
		window.addEventListener(CLOSE_BOTTOM_POPUPS_EVENT, handler);
		return () => window.removeEventListener(CLOSE_BOTTOM_POPUPS_EVENT, handler);
	}, []);

	const handleStep = (s) => {
		if (wrapperRef.current) wrapperRef.current?.classList?.add('hidden-step');

		if (s != 1) {
			setTimeout(() => {
				setStep(s);
				if (wrapperRef.current) wrapperRef.current?.classList?.remove('hidden-step');
			}, 350);
		} else {
			setStep(s);
		}
	};

	const onFinish = async (values) => {
		setBtnDisabled(true);
		return await api
			.post('subscribe', { ...values, receive_phone_sms: isChecked })
			.then(() => {
				setBtnDisabled(false);
				handleStep(3);
				setCookie('subscribed', 1);
			})
			.catch((err) => {
				setBtnDisabled(false);

				return Promise.reject(err);
			});
	};

	const handleEmailChange = (value) => {
		setEmail(value);
		setBtnDisabled(!value.trim());
	};

	const checkShow = useMemo(() => {
		if (typeof window === 'undefined') {
			return false;
		}

		let popupClosed = false;

		try {
			popupClosed = JSON.parse(sessionStorage.getItem('subscribe_popup_closed') || 'false');
		} catch {
			popupClosed = false;
		}

		return (
			!isLoggedIn &&
			!getCookie('subscribed') &&
			!popupClosed &&
			!pathname.includes(config.routes.cart.path) &&
			!pathname.includes(config.routes.signIn.path) &&
			!pathname.includes(config.routes.signUp.path) &&
			!pathname.includes(config.routes.forgotPassword.path) &&
			!pathname.includes(config.routes.resetPassword.path)
		);
	}, [isLoggedIn, pathname]);

	return checkShow ? (
		<SubscribePopupStyle
			ref={wrapperRef}
			id={'subscribe-wrapper'}
			className={`subscribe-wrapper ${hide ? 'hide' : ''} step-${step} ${onceAnim ? 'is-first' : ''}`}>
			<div className='subscribe-inner-wrapper'>
				<div className='top-wrapper'>
					{step === 3 ? (
						<Text
							text={'subscribeSuccessTitle'}
							className='uppercase h6 subscribe-discount-text'
						/>
					) : (
						<Text
							vars={{
								getText: <span className='extra-class'>{translate('get')}</span>,
								subscribeDiscount: <span className='extra-class'>10%</span>,
							}}
							text={'subscribePopupText'}
							className='uppercase h6 subscribe-discount-text'
						/>
					)}

					<div
						onClick={handleClose}
						className={`svg-block`}>
						{subscribePopupCloseSVG}
					</div>
				</div>

				{step === 3 ? (
					<Text
						vars={{
							email_key: <span className='font-montserrat-arm-bold'>{translate('email_key')}</span>,
						}}
						text={'subscribeSuccessDescription'}
						className='p4 subscribe-success-description'
					/>
				) : null}

				{step === 2 ? (
					<div className='step-2-wrapper'>
						<Form onSubmit={onFinish}>
							<Form.Item
								name={'email'}
								label={'emailLabel'}
								rules={[{ type: 'email' }]}>
								<Input
									value={email}
									onChange={(e) => handleEmailChange(e.target.value)}
									placeholder={'username@gmail.com'}
								/>
							</Form.Item>

							<Form.Item
								name={'phone'}
								label={'phoneLabel'}
								required={false}>
								<Input.PhoneNumber />
							</Form.Item>

							<AccountCustomCheckbox
								isText={'smsText'}
								hide={isChecked}
								setHide={setIsChecked}
							/>

							<Text
								vars={{
									privacyPolicy: (
										<Link
											external
											url={`/${selectedLang}${config.routes.privacyPolicy.path}`}
											className='capitalize font-montserrat-arm-bold privacy-link'>
											{translate('privacyPolicy')}
										</Link>
									),
								}}
								text={'subscribePrivacyPolicyText'}
								className='p4 subscribe-privacy-policy-text'
							/>

							<Button
								btnType={'full'}
								text={'subscribe'}
								className={'subscribe-btn-block'}
								type={'submit'}
								disabled={btnDisabled}
							/>
						</Form>
					</div>
				) : null}

				{step === 1 ? (
					<Button
						btnType={'full'}
						text={'subscribe'}
						className={'subscribe-btn-block'}
						onClick={() => handleStep(2)}
					/>
				) : null}

				{step === 3 ? (
					<Button
						btnType={'full'}
						text={'close'}
						className={'subscribe-btn-block'}
						onClick={handleClose}
					/>
				) : null}
			</div>
		</SubscribePopupStyle>
	) : null;
};

export default SubscribePopup;
